import React, { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FiSearch, FiX } from "react-icons/fi";
import SectionHead from "../ui/SectionHead";
import { skillCategories } from "../../data/resume";
import { fadeUp, staggerChildren, trackSpotlight } from "../../utils/motionPresets";
import "./stack.css";

const normalize = (value) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
const totalSkills = skillCategories.reduce((sum, c) => sum + c.skills.length, 0);

const Stack = () => {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const needle = normalize(query);

  const matches = useMemo(() => {
    if (!needle) return null;
    const set = new Set();
    skillCategories.forEach((category) =>
      category.skills.forEach((skill) => {
        if (normalize(skill).includes(needle) || normalize(category.label).includes(needle)) {
          set.add(`${category.id}:${skill}`);
        }
      })
    );
    return set;
  }, [needle]);

  return (
    <section className="section" id="stack">
      <div className="container">
        <SectionHead
          index="05"
          eyebrow="Stack"
          title={
            <>
              The <span className="serif">toolkit</span>
              <br />
              behind the work.
            </>
          }
          lede={`${totalSkills} technologies and skills, grouped by where they sit in the stack. Search to see if I've got what your project needs.`}
        />

        <div className="stack__search">
          <FiSearch className="stack__search-icon" aria-hidden="true" />
          <label htmlFor="skill-search" className="sr-only">
            Search skills
          </label>
          <input
            ref={inputRef}
            id="skill-search"
            type="search"
            placeholder="Search — try “docker”, “react” or “odoo”"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
          />
          {query ? (
            <button
              type="button"
              className="stack__clear"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              aria-label="Clear search"
            >
              <FiX />
            </button>
          ) : null}
          <span className="stack__result mono" aria-live="polite">
            {matches ? `${matches.size} match${matches.size === 1 ? "" : "es"}` : `${totalSkills} total`}
          </span>
        </div>

        <motion.div
          className="stack"
          variants={staggerChildren}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {skillCategories.map((category) => {
            const hits = matches
              ? category.skills.filter((skill) => matches.has(`${category.id}:${skill}`)).length
              : category.skills.length;
            return (
              <motion.div
                key={category.id}
                variants={fadeUp}
                className={`card stack__group stack__group--${category.id} ${
                  matches && !hits ? "is-dimmed" : ""
                }`}
                onPointerMove={trackSpotlight}
              >
                <div className="stack__group-head">
                  <h3>{category.label}</h3>
                  <span className="mono">{String(hits).padStart(2, "0")}</span>
                </div>
                <ul className="stack__skills">
                  {category.skills.map((skill) => {
                    const hit = matches?.has(`${category.id}:${skill}`);
                    return (
                      <li
                        key={skill}
                        className={`stack__skill ${hit ? "is-hit" : ""} ${
                          matches && !hit ? "is-miss" : ""
                        }`}
                      >
                        {skill}
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Stack;
